import { useState } from "react";
import { useDeleteRiderMutation, useFetchRidersQuery } from "../store";
import Pagination from "../components/common/Pagination";
import { FaEdit, FaPlus, FaTrash, FaMotorcycle, FaBicycle, FaCar } from "react-icons/fa";
import TextField from "../components/common/TextField";
import useDebounce from "../hooks/useDebounce";
import { Link } from "react-router-dom";
import { Rider, Status, TransportType, TransportTypeLabels } from "../types";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";

const getTransportIcon = (type: TransportType) => {
  switch (type) {
    case TransportType.BIKE:
      return <FaBicycle className="text-xl" />;
    case TransportType.CAR:
      return <FaCar className="text-xl" />;
    case TransportType.SCOOTER:
      return <FaMotorcycle className="text-xl" />;
    default:
      return <FaMotorcycle className="text-xl" />;
  }
};

const Riders: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [name, setName] = useState<string>("");
  const debouncedName = useDebounce(name, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [riderToDelete, setRiderToDelete] = useState<Rider | null>(null);

  const [deleteRider] = useDeleteRiderMutation();

  const confirmDelete = (riderToDelete: Rider) => {
    setRiderToDelete(riderToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRider = async () => {
    if (!riderToDelete) return;
    const result = await deleteRider(riderToDelete._id);
    if (!result.error) {
      showToast("Livreur supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRiderToDelete(null);
  };

  const { data: riders, isLoading } = useFetchRidersQuery({
    page,
    perPage,
    name: debouncedName,
  });

  return (
    <div className="w-full mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Livreurs</h1>
          <p className="text-gray-500 mt-1">
            Gérez votre flotte de livreurs
          </p>
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="w-full md:w-64">
            <TextField
              id="search"
              value={name}
              onChange={(e: any) => setName(e.target.value)}
              placeholder="Rechercher un livreur..."
            />
          </div>
          <Link
            to="/riders/create"
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-opacity"
          >
            <FaPlus /> Nouveau Livreur
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">Livreur</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4 text-center">Véhicule</th>
                <th className="px-6 py-4 text-center">Statut</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    Chargement...
                  </td>
                </tr>
              ) : riders?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    Aucun livreur trouvé.
                  </td>
                </tr>
              ) : (
                riders?.data?.map((row) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-lg overflow-hidden shrink-0 border border-gray-200">
                          {row.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {row.fullName}
                          </div>
                          {/* Email is not available on Rider list type in current API */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700 font-mono text-xs">
                      {row.phone}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center justify-center">
                        <div
                          className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${
                            row.transportType === TransportType.BIKE
                              ? "bg-green-50 text-green-700 border-green-200"
                              : row.transportType === TransportType.SCOOTER
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-purple-50 text-purple-700 border-purple-200"
                          }`}
                        >
                          {getTransportIcon(row.transportType)}
                          <span>{TransportTypeLabels[row.transportType]}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${
                            row.status === Status.ONLINE
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${
                              row.status === Status.ONLINE
                                ? "bg-green-500 animate-pulse"
                                : "bg-gray-400"
                            }`}
                          />
                          {row.status === Status.ONLINE
                            ? "En ligne"
                            : "Hors ligne"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={row._id}
                          className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          title="Modifier"
                        >
                          <FaEdit size={14} />
                        </Link>
                        <button
                          onClick={() => confirmDelete(row)}
                          className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                          title="Supprimer"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 px-6 py-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: riders?.paginationInfo.count || 0,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg shadow-sm"
                onClick={handleDeleteRider}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-500">
              Êtes-vous sûr de vouloir supprimer{" "}
              <span className="font-bold text-gray-800">
                {riderToDelete?.fullName}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Riders;
